import { CheckCircle2, Clock, ExternalLink, XCircle } from "lucide-react";
import useUser from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import { cn } from "~/lib/utils";
import { nextGenDatasetDetailPath } from "~/router/constants";
import { formatDateTime } from "~/utils/date";
import type { ToolUIProps } from "./ToolUIContext";

interface AccessRequestItem {
  id: string;
  cohort_name: string;
  parent_dataset_id: string;
  child_dataset_id: string | null;
  status: "pending" | "processing" | "approved" | "denied" | "failed" | "cancelled";
  intended_use: string;
  created_at: string;
  updated_at: string;
}

interface AccessRequestsResult {
  items: AccessRequestItem[];
  total: number;
}

const STATUS_CONFIG = {
  pending: { icon: Clock, label: "Pending", color: "text-yellow-600 dark:text-yellow-400" },
  processing: { icon: Clock, label: "Processing", color: "text-yellow-600 dark:text-yellow-400" },
  approved: { icon: CheckCircle2, label: "Approved", color: "text-green-600 dark:text-green-400" },
  denied: { icon: XCircle, label: "Denied", color: "text-red-600 dark:text-red-400" },
  failed: { icon: XCircle, label: "Failed", color: "text-red-600 dark:text-red-400" },
  cancelled: { icon: XCircle, label: "Cancelled", color: "text-muted-foreground" },
} as const;

function buildDatasetUrl(childDatasetId: string): string {
  return `${window.location.origin}${nextGenDatasetDetailPath(childDatasetId)}/overview`;
}

function buildFetchUrl(
  parentDatasetId: string | undefined,
  userEmail: string | undefined
): string | null {
  if (!userEmail || !parentDatasetId) return null;
  const params = new URLSearchParams({
    user_id: userEmail,
    parent_dataset_id: parentDatasetId,
  });
  return `/api/cohort-explorer/v1/access-requests?${params}`;
}

function parseResult(result: string): AccessRequestsResult | null {
  try {
    return JSON.parse(result) as AccessRequestsResult;
  } catch {
    return null;
  }
}

function StatusBadge({ status }: { status: AccessRequestItem["status"] }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  return (
    <span className={cn("inline-flex items-center gap-1", config.color)}>
      <Icon className="size-3.5 shrink-0" aria-hidden />
      {config.label}
    </span>
  );
}

function Skeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card animate-pulse">
      <div className="px-4 pt-4 pb-2">
        <div className="h-4 w-36 rounded bg-muted" />
      </div>
      <div className="px-4 pb-4 space-y-2">
        <div className="h-12 rounded bg-muted" />
        <div className="h-12 rounded bg-muted" />
      </div>
    </div>
  );
}

export function AccessRequestsToolUI({ result, isRunning }: ToolUIProps) {
  const user = useUser();
  const toolResult = result ? parseResult(result) : null;
  const parentDatasetId = toolResult?.items[0]?.parent_dataset_id;
  const fetchUrl = buildFetchUrl(parentDatasetId, user?.email);

  const { data: liveData, isLoading } = useFetcher<AccessRequestsResult>(
    result && fetchUrl ? fetchUrl : null
  );

  const data = liveData ?? toolResult;

  if (isLoading && !data) return <Skeleton />;

  if (data) {
    if (data.items.length === 0) {
      return (
        <div className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          No access requests found.
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm">
        <div className="px-4 pt-4 pb-2">
          <p className="font-bold text-base leading-tight">
            Access Requests
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              ({data.total} total)
            </span>
          </p>
        </div>
        <div className="px-4 pb-4 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="pb-2 pr-3 font-medium">Cohort</th>
                <th className="pb-2 pr-3 font-medium">Status</th>
                <th className="pb-2 pr-3 font-medium">Submitted</th>
                <th className="pb-2 pr-3 font-medium">Intended Use</th>
                <th className="pb-2 font-medium">Exported Dataset</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.id} className="border-b border-border/50 last:border-0">
                  <td className="py-2 pr-3 whitespace-nowrap">{item.cohort_name}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">
                    {formatDateTime(item.created_at, { hour: "numeric" })}
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground max-w-[200px] truncate">
                    {item.intended_use}
                  </td>
                  <td className="py-2">
                    {item.status === "approved" && item.child_dataset_id ? (
                      <a
                        href={buildDatasetUrl(item.child_dataset_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        View
                        <ExternalLink className="size-3" aria-hidden />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (isRunning) return <Skeleton />;
  return null;
}
